import {
  UserService,
  type UserSvcPaginationMeta,
  type UserSvcUser,
} from '@kp/rest-api-javascript-sdk';

import type { SortingFilter } from '../common';

/**
 * Whether the account can sign in. Not on `UserSvcUser` yet — see `User`.
 *
 * `pending` is accepted but not expected on this endpoint: a user only
 * appears in the list once they belong to the tenant, so an unaccepted
 * invitation is a row on the Pending Invitations tab, not here.
 */
export const UserStatus = {
  Active: 'active',
  Inactive: 'inactive',
  Pending: 'pending',
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

/**
 * Tenant user as the Users tab needs it.
 *
 * `status` and `lastActiveAt` are pending additions to GET /user/api/users
 * and are absent from `UserSvcUser`. The runtime mock supplies them; against
 * the live backend both read as `undefined`. They stay optional until the
 * service ships them, so no consumer can assume they are present.
 */
export type User = UserSvcUser & {
  status?: UserStatus;
  /** ISO 8601 timestamp; absent when the user has never been active. */
  lastActiveAt?: string;
};

export type UserSort = 'firstName' | 'lastName' | 'email' | 'createdAt';

export type UserFilters = SortingFilter<UserSort> & { roles?: string[] };

/**
 * List the tenant's users.
 *
 * Two request-side parts of the target contract cannot be sent yet, because
 * `@kp/rest-api-javascript-sdk` has to be regenerated first:
 *   - free-text search (`q`) — `UserService.getUsersSearch` is a different
 *     endpoint returning `UserSvcUserSummary` (no roles), so it cannot back
 *     this table;
 *   - sorting by `status` / `lastActiveAt` — `sort` is a closed union here.
 * Response-side additions need no SDK change; see `User`.
 */
export const getUsers = async (
  filter: UserFilters = {},
): Promise<{ meta?: UserSvcPaginationMeta; data?: User[] }> =>
  UserService.getUsers({
    query: {
      skip: filter.skip,
      limit: filter.limit,
      sort: filter.sort,
      sortDir: filter.sortDir,
      roles: filter.roles,
    },
  });

/**
 * Get details of a user
 *
 * @param userId       The id of a user that will be fetched
 */
export const getUser = (userId: string) =>
  UserService.getUsersByUserId({ path: { userId } });

/**
 * Remove a user from tenant and delete their invitation
 *
 * @param userId       The id of a user that will be removed
 */
export const deleteUser = (userId: string) =>
  UserService.deleteUsersByUserId({ path: { userId } });
